import { Box, Button, Typography } from '@mui/material';
import React, { useLayoutEffect, useRef, useState } from 'react';

const LINE_HEIGHT = 1.45;
/** Visible text height before “(N More)” — ~1.5 lines of body2. */
const CLAMP_LINE_FRACTION = 1.5;

const summaryBodySx = {
    mt: 0.5,
    lineHeight: LINE_HEIGHT,
    wordBreak: 'break-word' as const,
};

const inlineTextButtonSx = {
    display: 'inline-flex',
    alignItems: 'baseline',
    p: 0,
    minWidth: 0,
    minHeight: 0,
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    verticalAlign: 'baseline',
    color: 'primary.main',
    ml: 0.5,
    '&:hover': { bgcolor: 'transparent' },
};

/**
 * Read-only region list: shows ~1.5 lines of comma-separated suburbs, then
 * “(N More)” where N is how many suburbs are not shown (expand for full list).
 */
export function ServiceAreaRegionSummary({
    text,
    areaNames,
    enableClamp,
}: {
    text: string;
    /** Names in list order; used for counts and line fitting when {@link enableClamp}. */
    areaNames?: readonly string[];
    enableClamp: boolean;
}) {
    const names = areaNames ?? [];
    const namesKey = names.join('\u0001');

    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const prevNamesKeyRef = useRef<string | null>(null);
    const [expanded, setExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(names.length);

    useLayoutEffect(() => {
        const listKeyChanged = prevNamesKeyRef.current !== namesKey;
        if (listKeyChanged) {
            prevNamesKeyRef.current = namesKey;
            setExpanded(false);
        }

        if (!enableClamp || names.length === 0) {
            setVisibleCount(names.length);
            return;
        }

        const measureCollapsed = listKeyChanged || !expanded;
        if (!measureCollapsed) {
            setVisibleCount(names.length);
            return;
        }

        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const run = () => {
            const w = container.clientWidth;
            if (w <= 0) return;

            measure.style.width = `${w}px`;

            const cs = window.getComputedStyle(measure);
            const fontSize = parseFloat(cs.fontSize);
            const lineHeightPx =
                cs.lineHeight === 'normal'
                    ? fontSize * LINE_HEIGHT
                    : parseFloat(cs.lineHeight);
            /* Small buffer: measured plain text vs semibold inline button width */
            const maxH = lineHeightPx * CLAMP_LINE_FRACTION + 4;

            /** Fit suburbs + inline “(N More)” in the same ~1.5 lines (not suburbs alone). */
            const collapsedLineText = (mid: number) => {
                const body = names.slice(0, mid).join(', ');
                if (mid >= names.length) return body;
                return `${body} (${names.length - mid} More)`;
            };

            let lo = 1;
            let hi = names.length;
            let best = 0;
            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2);
                measure.textContent = collapsedLineText(mid);
                const h = measure.scrollHeight;
                if (h <= maxH) {
                    best = mid;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            if (best === 0 && names.length > 0) {
                best = 1;
            }
            setVisibleCount(best);
        };

        run();
        const ro = new ResizeObserver(run);
        ro.observe(container);
        return () => ro.disconnect();
    }, [enableClamp, expanded, namesKey, names.length]);

    if (!enableClamp) {
        return (
            <Typography
                variant='body2'
                color='text.secondary'
                sx={summaryBodySx}
            >
                {text}
            </Typography>
        );
    }

    if (names.length === 0) {
        return (
            <Typography
                variant='body2'
                color='text.secondary'
                sx={summaryBodySx}
            >
                {text}
            </Typography>
        );
    }

    const hiddenCount = expanded ? 0 : Math.max(0, names.length - visibleCount);
    const displayText = expanded
        ? names.join(', ')
        : names.slice(0, visibleCount).join(', ');

    return (
        <Box ref={containerRef} sx={{ minWidth: 0, position: 'relative' }}>
            <Typography
                ref={measureRef}
                variant='body2'
                color='text.secondary'
                component='div'
                aria-hidden
                sx={{
                    ...summaryBodySx,
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    mt: 0,
                }}
            />
            <Typography
                variant='body2'
                color='text.secondary'
                component='div'
                sx={summaryBodySx}
            >
                <Box component='span' sx={{ wordBreak: 'break-word' }}>
                    {displayText}
                </Box>
                {!expanded && hiddenCount > 0 ? (
                    <Button
                        type='button'
                        variant='text'
                        size='small'
                        onClick={() => setExpanded(true)}
                        aria-expanded={false}
                        sx={inlineTextButtonSx}
                    >
                        {`(${hiddenCount} More)`}
                    </Button>
                ) : null}
                {expanded && names.length > visibleCount ? (
                    <Button
                        type='button'
                        variant='text'
                        size='small'
                        onClick={() => setExpanded(false)}
                        aria-expanded
                        sx={inlineTextButtonSx}
                    >
                        Hide
                    </Button>
                ) : null}
            </Typography>
        </Box>
    );
}
